import { nil, NO, UITextView, UIView, UIViewAddControlEventTargetObject } from "uicore-ts"
import { LooseObject } from "uicore-ts/scripts/UIView"
import { RHPageFocusManager } from "../RH/Keyboard navigation/RHPageFocusManager"

// This one is reliant on styles.css, which is no longer included. Use RHCheckbox instead.
// This class is just kept as a base class
export class CBCheckbox extends UIView {
    
    checkbox: UIView
    titleLabel: UITextView
    
    constructor(
        elementID?: string,
        viewHTMLElement?: HTMLElement & LooseObject,
        elementType: string = "label"
    ) {
        
        super(elementID, nil, elementType)
        
        this.checkbox = new UIView(this.elementID + "Checkbox", nil, "input")
        this.checkbox.viewHTMLElement.setAttribute("type", "checkbox")
        
        this.addSubview(this.checkbox)
        
        this.titleLabel = new UITextView(this.elementID + "TitleLabel", UITextView.type.span)
        
        this.addSubview(this.titleLabel)
        
        
        this.titleLabel.userInteractionEnabled = NO
        
        this.titleLabel.style.overflow = "visible"
        this.titleLabel.style.lineHeight = "1.5"
        
        this.style.cursor = "pointer"
        
        // When the inner input receives focus, forward Focus/Blur control events
        // to the outer view so the framework's focus styling is applied correctly.
        this.checkbox.addTargetForControlEvent(UIView.controlEvent.Focus, () => {
            this.sendControlEventForKey(UIView.controlEvent.Focus)
        })
        this.checkbox.addTargetForControlEvent(UIView.controlEvent.Blur, () => {
            this.sendControlEventForKey(UIView.controlEvent.Blur)
        })
        
        // Enter: toggle the checkbox / select the radio button, then fire the
        // same events as a mouse click so CBButtonGroup and other listeners react.
        // Space already works via the native change event path.
        this.checkbox.addTargetForControlEvent(UIView.controlEvent.EnterDown, (sender, event) => {
            event.preventDefault()
            const input = this.checkbox.viewHTMLElement as HTMLInputElement
            const isRadio = input.type === "radio"
            if (isRadio && input.checked) {
                // Already selected — nothing to do
                return
            }
            input.checked = isRadio ? true : !input.checked
            this.sendControlEventForKey(CBCheckbox.controlEvent.SelectionChange, event)
            this.sendControlEventForKey(UIView.controlEvent.PointerUpInside, event)
            this.sendControlEventForKey(CBCheckbox.controlEvent.PrimaryActionTriggered, event)
        })
        
        // Arrow keys: move focus to adjacent item in the managed sequence.
        // Prevents the browser from natively moving focus between radio inputs.
        const moveFocusForward = (sender: UIView, event: Event) => {
            event.preventDefault()
            RHPageFocusManager.shared.handleTabEvent(nil)
        }
        const moveFocusBackward = (sender: UIView, event: Event) => {
            event.preventDefault()
            RHPageFocusManager.shared.handleTabEvent(nil)
        }
        this.checkbox.addTargetForControlEvent(UIView.controlEvent.RightArrowDown, moveFocusForward)
        this.checkbox.addTargetForControlEvent(UIView.controlEvent.DownArrowDown, moveFocusForward)
        this.checkbox.addTargetForControlEvent(UIView.controlEvent.LeftArrowDown, moveFocusBackward)
        this.checkbox.addTargetForControlEvent(UIView.controlEvent.UpArrowDown, moveFocusBackward)
        
        this.viewHTMLElement.onchange = (event) => {
            this.sendControlEventForKey(CBCheckbox.controlEvent.SelectionChange, event)
            // Also fire PointerUpInside on genuine user interactions so CBButtonGroup
            // (which listens to EnterDown|PointerUpInside) reacts to Space-key toggles.
            if ((event as Event).isTrusted) {
                this.sendControlEventForKey(UIView.controlEvent.PointerUpInside, event)
                this.sendControlEventForKey(CBCheckbox.controlEvent.PrimaryActionTriggered, event)
            }
        }
        
    }
    
    
    static override controlEvent = Object.assign({}, UIView.controlEvent, {
        
        "SelectionChange": "SelectionChange",
        "PrimaryActionTriggered": "PrimaryActionTriggered"
        
    })
    
    // @ts-ignore
    get controlEventTargetAccumulator(): UIViewAddControlEventTargetObject<typeof CBCheckbox> {
        return super.controlEventTargetAccumulator as UIViewAddControlEventTargetObject<typeof CBCheckbox>
    }
    
    /**
     * Delegates focus to the inner checkbox input element so the browser's
     * native Space-key handling (toggle + change event) works correctly, and
     * so the `:focus` ring appears on the input rather than the outer label.
     */
    override focus() {
        this.checkbox.viewHTMLElement.focus()
    }
    
    
    /**
     * Routes tabIndex to the inner input so the browser's Tab sequence lands
     * directly on the input (where keyboard events fire) rather than the outer label.
     */
    override set tabIndex(index: number) {
        this.checkbox.viewHTMLElement.setAttribute("tabindex", "" + index)
    }
    
    override get tabIndex(): number {
        return Number(this.checkbox.viewHTMLElement.getAttribute("tabindex"))
    }
    
    
    get isStatic() {
        
        const result = this.checkbox.viewHTMLElement.classList.contains("staticCheckbox")
        
        return result
        
    }
    
    set isStatic(isStatic: boolean) {
        
        if (isStatic) {
            
            this.checkbox.viewHTMLElement.classList.add("staticCheckbox")
            
        }
        else {
            
            this.checkbox.viewHTMLElement.classList.remove("staticCheckbox")
            
        }
        
        
    }
    
    
    get selected() {
        
        return (this.checkbox.viewHTMLElement as HTMLInputElement).checked
        
    }
    
    set selected(selected: boolean) {
        
        (this.checkbox.viewHTMLElement as HTMLInputElement).checked = selected
        
    }
    
    
    /**
     * The indeterminate state is a purely visual indicator — it does not affect
     * the underlying `checked` value or the `SelectionChange` event. It is used
     * to signal partial selection (e.g. some but not all children selected in a
     * tree). Maps directly to `HTMLInputElement.indeterminate`.
     */
    get indeterminate(): boolean {
        return (this.checkbox.viewHTMLElement as HTMLInputElement).indeterminate
    }
    
    set indeterminate(indeterminate: boolean) {
        (this.checkbox.viewHTMLElement as HTMLInputElement).indeterminate = indeterminate
    }
    
    
    override layoutSubviews() {
        
        super.layoutSubviews()
        
        const titleWidth = this.bounds.width - 35
        this.titleLabel.setMaxSizes(nil, titleWidth)
        
        this.titleLabel.textPrefix = "<span style=\"position: absolute; overflow: hidden; left: 0; top: 0;text-overflow: ellipsis; white-space: pre; padding-left: 35px; width: " +
            titleWidth + "px;\">"
        
        this.titleLabel.textSuffix = "</span>"
        
        this.hoverText = this.titleLabel.text.replace(
            this.titleLabel.textPrefix,
            ""
        ).replace(this.titleLabel.textSuffix, "")
        
        
    }
    
    
}
